import { useMutation, useQuery } from '@tanstack/react-query';
import { dataLimits } from 'constants';
import { API_ENDPOINTS } from 'constants/endpoints';
import { del, get, handleError, handleResponse, post, put } from './base.service';

export const useAddAPostMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.ADD_POST, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetPostMutation = () =>
	useMutation({
		mutationFn: (searchString: any) => {
			return get(`${API_ENDPOINTS.POST_LIST}?${searchString}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetStaticPostMutation = () =>
	useMutation({
		mutationFn: (searchString: any) => {
			return get(`${API_ENDPOINTS.STATIC_POST_LIST}?${searchString}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetPostQuery = ({ isFetch, searchString, userToken }: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.POST_LIST}?${searchString}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-post-list', isFetch, searchString, userToken],
		refetchOnWindowFocus: false,
		enabled: !!isFetch && !!userToken,
	});

export const useGetStaticPostQuery = ({ isFetch, searchString }: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.STATIC_POST_LIST}?${searchString}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-static-post-list', isFetch, searchString],
		refetchOnWindowFocus: false,
		enabled: !!isFetch,
	});

export const useGetHashtagsQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.HASHTAG_LIST}?${params}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-hashtag-list'],
		enabled: !!params,
	});

export const useGetMentionUsersQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.MENTIONS_USERS_LIST}?${params}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-mention-users-list'],
		enabled: !!params,
	});

export const usePostLikeMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.POST_LIKE, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetpostLikedUsersQuery = ({
	id,
	currentPage,
}: {
	id: string;
	currentPage: number;
}) =>
	useQuery({
		queryKey: ['get-post-liked-users', id, currentPage],
		queryFn: async () => {
			const queryString = `${id}?order=DESC&page=${currentPage}&limit=${dataLimits.postLikedModal}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;
			try {
				const response = await get(`${API_ENDPOINTS.POST_LIKED}/${queryString}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
				throw err;
			}
		},
		enabled: !!id && !!currentPage,
		refetchOnWindowFocus: false,
	});

export const useFeedDetailsQuery = (id: number | null) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.ADD_POST}/${id}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['feed-details'],
		enabled: !!id,
		refetchOnWindowFocus: false,
		staleTime: 0,
	});

export const useFeedDetailsMutation = () =>
	useMutation({
		mutationFn: async (id: any) => {
			return await get(`${API_ENDPOINTS.ADD_POST}/${id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useAddCommentMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.Add_COMMENT, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useCheckHashTagsMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.CHECK_HASHTAGS, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useFeedCommentsQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.Add_COMMENT}/${payload?.searchString}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['feed-comments'],
		enabled: !!payload?.id,
		refetchOnWindowFocus: false,
	});

export const useLikeCommentMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.LIKE_COMMENT, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useFollowHashtagMutation = () =>
	useMutation({
		mutationFn: (id: any) => {
			return post(API_ENDPOINTS.FOLLOW_HASHTAG, { hashTagId: id });
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useCommentRepliesMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return get(
				`${API_ENDPOINTS.COMMENT_REPLIES}/${payload?.id}?page=${payload?.pageForReply}&limit=${dataLimits?.feedCommentReply}`,
			);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useDeleteCommentMutation = () =>
	useMutation({
		mutationFn: (id: number) => {
			return del(`${API_ENDPOINTS.DELETE_COMMENT}/${id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useReportMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(`${API_ENDPOINTS.REPORT_URL}`, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetSharePostUsersQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.SHARE_POST_USERS}/${payload?.id}?order=${payload.order}&page=${payload.currentPage}&limit=${payload?.limit}&keyword=${payload?.keyword}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['share-post'],
		refetchOnWindowFocus: false,
	});

export const useShareMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(`${API_ENDPOINTS.SHARE_POST}`, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetHashtagDetailsQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.HASHTAG_PROFILE}?order=DESC&page=${payload.page}&limit=${payload.limit}&socialPostType=hashtag-posts&hashTagId=${payload?.id}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['hashtag-profile', payload?.id, payload?.page],
		refetchOnWindowFocus: false,
		enabled: !!payload?.id,
	});

export const useGetHashtagFollowListQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.FOLLOW_HASHTAG}?order=DESC&page=${payload.page}&limit=${payload.limit}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['hashtag-follow-list'],
		refetchOnWindowFocus: false,
	});

export const useGetStaticHashtagFollowListQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.STATIC_FOLLOW_HASHTAG}?order=DESC&page=${payload.page}&limit=${payload.limit}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['static-hashtag-follow-list'],
		refetchOnWindowFocus: false,
	});

export const useDeleteFeedMutation = () =>
	useMutation({
		mutationFn: (id: number | null = null) => {
			return del(`${API_ENDPOINTS.ADD_POST}/${id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetReportReasonsQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_REPORT_REASONS}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['report-reason-list'],
		refetchOnWindowFocus: false,
	});

export const useFeedUpdateMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return put(`${API_ENDPOINTS.ADD_POST}/${payload?.id}`, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useUserActivityQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.USER_ACTIVITY}/${payload}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['user-activities'],
		refetchOnWindowFocus: false,
	});
